import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import theme from '../../../theme';
import { useI18n } from '../../../i18n/useI18n';
import AppButton from '../../../components/AppButton';

const COLOR_PALETTE: Array<{ key: string; value: string }> = [
  { key: 'primary', value: theme.colors.primary },
  { key: 'accent', value: theme.colors.accent },
  { key: 'success', value: theme.colors.success },
  { key: 'error', value: theme.colors.error },
  { key: 'secondary', value: theme.colors.secondary },
  { key: 'primaryVariant', value: theme.colors.primaryVariant },
  { key: 'muted', value: theme.colors.muted },
  { key: 'textPrimary', value: theme.colors.textPrimary },
];

type Props = {
  initial?: { name?: string; color?: string; type?: 'income' | 'expense' };
  onCancel: () => void;
  onSave: (payload: { name: string; color?: string; type: 'income' | 'expense' }) => void;
  lang?: string;
};

export default function CategoryForm({ initial, onCancel, onSave, lang, fullScreen = false }: Props & { fullScreen?: boolean }) {
  const [name, setName] = useState(initial?.name || '');
  const [color, setColor] = useState(initial?.color || '');
  const [type, setType] = useState<'income' | 'expense'>(initial?.type || 'expense');
  const { t } = useI18n(lang);
  const { width } = useWindowDimensions();
  const isWide = width >= 700;

  // ensure containerStyle is Array<any> and cast responsive style objects to avoid typing issues
  const containerStyle: Array<any> = [styles.container];
  if (fullScreen && !isWide) {
    containerStyle.push({ width: '100%', maxWidth: undefined, paddingHorizontal: 16, borderRadius: 0 } as any);
  } else if (!isWide) {
    containerStyle.push({ alignSelf: 'stretch' } as any);
  } else {
    containerStyle.push({ alignSelf: 'center', maxWidth: 420 } as any);
  }

  useEffect(() => {
    setName(initial?.name || '');
    setColor(initial?.color || '');
    setType(initial?.type || 'expense');
  }, [initial]);

  return (
    <View style={[...containerStyle, { backgroundColor: theme.colors.background }]}> 
      <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('name')}</Text>
      <TextInput value={name} onChangeText={setName} style={[styles.input, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface, color: theme.colors.textPrimary }]} />

      <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('type')}</Text>
      <View style={styles.row}>
        <AppButton title={t('expense')} onPress={() => setType('expense')} tone="primary" variant={type === 'expense' ? 'solid' : 'outline'} style={{ flex: 1 }} />
        <View style={{ width: 12 }} />
        <AppButton title={t('income')} onPress={() => setType('income')} tone="primary" variant={type === 'income' ? 'solid' : 'outline'} style={{ flex: 1 }} />
      </View>

      <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('color')}</Text>
      <View style={styles.palette}>
        {COLOR_PALETTE.map((c) => {
          const selected = (color || '').toLowerCase() === c.value.toLowerCase();
          return (
            <TouchableOpacity
              key={c.key}
              onPress={() => setColor(c.value)}
              accessibilityRole="button"
              accessibilityLabel={`${t('color')} ${c.key}`}
              style={[
                styles.swatch,
                {
                  backgroundColor: c.value,
                  borderColor: selected ? theme.colors.textPrimary : theme.colors.border,
                  borderWidth: selected ? 2 : 1,
                },
              ]}
            />
          );
        })}
        <TouchableOpacity
          onPress={() => setColor('')}
          accessibilityRole="button"
          accessibilityLabel={`${t('color')} none`}
          style={[
            styles.swatch,
            {
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.border,
              borderWidth: 1,
              justifyContent: 'center',
              alignItems: 'center',
            },
          ]}
        >
          <Text style={{ color: theme.colors.textSecondary, fontWeight: '700' }}>×</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.row}>
        <AppButton title={t('cancel')} onPress={onCancel} tone="secondary" variant="outline" style={{ flex: 1 }} />
        <View style={{ width: 12 }} />
        <AppButton title={t('save')} onPress={() => onSave({ name: name.trim(), type, color: color.trim() || undefined })} disabled={!name.trim()} tone="primary" style={{ flex: 1 }} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { padding: 12 },
  label: { marginTop: 8, marginBottom: 4, fontWeight: '600' },
  input: { height: 40, borderWidth: 1, paddingHorizontal: 8, borderRadius: 4 },
  row: { flexDirection: 'row', marginTop: 12 },
  palette: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 4 },
  swatch: {
    width: 32,
    height: 32,
    borderRadius: 8,
    marginRight: 8,
    marginBottom: 8,
  },
});
